import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronDown, X, Loader2, Check, History } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import type { PlaceholderInfo } from "@/lib/query-placeholders";
import { buildUniqueValuesQuery, extractTableFromSql } from "@/lib/query-placeholders";
import { getPlaceholderHistory, addPlaceholderHistory } from "@/lib/placeholder-history";

interface Props {
  placeholder: PlaceholderInfo;
  value: string;
  onChange: (value: string) => void;
  /** The full SQL query — used to extract table name for fetching options. */
  sql: string;
}

/** Fetch unique column values via mcp-api. */
async function fetchUniqueValues(sql: string, columnName: string): Promise<string[]> {
  const tableName = extractTableFromSql(sql);
  if (!tableName || !columnName) return [];

  // Use the raw column name (strip table prefix if present)
  const colOnly = columnName.includes(".") ? columnName.split(".")[1] : columnName;
  const query = buildUniqueValuesQuery(tableName, colOnly);

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const headers = await buildEdgeFunctionHeaders();
    const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
      method: "POST",
      headers,
      body: JSON.stringify([query]),
    });
    if (!res.ok) return [];
    const data = await res.json();
    let rows: any[] = [];
    if (Array.isArray(data)) {
      const first = data[0];
      rows = Array.isArray(first) ? first : first?.rows || first?.result || data;
    } else if (data?.results) {
      const first = Array.isArray(data.results) ? data.results[0] : data.results;
      rows = Array.isArray(first) ? first : first?.rows || first?.result || [];
    } else if (data?.rows) {
      rows = data.rows;
    }
    return rows
      .map((r: any) => {
        const val = Object.values(r)[0];
        return val !== null && val !== undefined ? String(val) : null;
      })
      .filter((v): v is string => v !== null);
  } catch {
    return [];
  }
}

const PlaceholderInput = ({ placeholder: ph, value, onChange, sql }: Props) => {
  const needsOptions = ph.displayType === "dropdown" || ph.displayType === "multiselect" || ph.displayType === "autocomplete";
  const [options, setOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsFetched, setOptionsFetched] = useState(false);
  const [open, setOpen] = useState(false);
  const fetchRef = useRef<string | null>(null);

  // History from localStorage
  const [history, setHistory] = useState<string[]>(() => getPlaceholderHistory(ph.name));

  // Reload history when popover opens (in case another instance saved something)
  useEffect(() => {
    if (open) setHistory(getPlaceholderHistory(ph.name));
  }, [open, ph.name]);

  const saveToHistory = useCallback(
    (val: string) => {
      if (!val.trim()) return;
      addPlaceholderHistory(ph.name, val);
      setHistory(getPlaceholderHistory(ph.name));
    },
    [ph.name],
  );

  // Fetch options when component needs them
  useEffect(() => {
    if (!needsOptions || optionsFetched) return;
    if (!ph.columnName) return;
    const key = `${ph.columnName}:${sql.slice(0, 100)}`;
    if (fetchRef.current === key) return;
    fetchRef.current = key;
    setLoadingOptions(true);
    fetchUniqueValues(sql, ph.columnName).then((vals) => {
      setOptions(vals);
      setOptionsFetched(true);
      setLoadingOptions(false);
    });
  }, [needsOptions, ph.columnName, sql, optionsFetched]);

  // -- Dropdown (single select) --
  if (ph.displayType === "dropdown") {
    // History items not already in options
    const recentItems = history.filter((h) => !options.includes(h));

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-8 w-full justify-between text-sm font-normal"
          >
            <span className="truncate">{value || `Select ${ph.name}…`}</span>
            {loadingOptions ? (
              <Loader2 className="ml-1 h-3.5 w-3.5 shrink-0 animate-spin" />
            ) : (
              <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${ph.name}…`} className="h-8" />
            <CommandList>
              <CommandEmpty>No results.</CommandEmpty>
              {recentItems.length > 0 && (
                <CommandGroup heading="Recent">
                  {recentItems.map((opt) => (
                    <CommandItem
                      key={`recent-${opt}`}
                      value={opt}
                      onSelect={() => {
                        onChange(opt);
                        saveToHistory(opt);
                        setOpen(false);
                      }}
                    >
                      <History className={cn("mr-2 h-3.5 w-3.5 text-muted-foreground")} />
                      {opt}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              <CommandGroup heading={recentItems.length > 0 ? "All values" : undefined}>
                {options.map((opt) => (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => {
                      onChange(opt);
                      saveToHistory(opt);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-3.5 w-3.5", value === opt ? "opacity-100" : "opacity-0")} />
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  // -- Multiselect --
  if (ph.displayType === "multiselect") {
    const selected = value ? value.split(",").map((v) => v.trim()).filter(Boolean) : [];

    const toggleOption = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((v) => v !== opt)
        : [...selected, opt];
      const joined = next.join(", ");
      onChange(joined);
      saveToHistory(joined);
    };

    const removeOption = (opt: string) => {
      const joined = selected.filter((v) => v !== opt).join(", ");
      onChange(joined);
    };

    // History items that look like multiselect values (comma-separated)
    const recentSets = history.filter((h) => h.includes(",") || !options.includes(h));

    return (
      <div className="space-y-1.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="h-8 w-full justify-between text-sm font-normal"
            >
              <span className="truncate">
                {selected.length > 0 ? `${selected.length} selected` : `Select ${ph.name}…`}
              </span>
              {loadingOptions ? (
                <Loader2 className="ml-1 h-3.5 w-3.5 shrink-0 animate-spin" />
              ) : (
                <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder={`Search ${ph.name}…`} className="h-8" />
              <CommandList>
                <CommandEmpty>No results.</CommandEmpty>
                {recentSets.length > 0 && (
                  <CommandGroup heading="Recent selections">
                    {recentSets.map((combo) => (
                      <CommandItem
                        key={`recent-${combo}`}
                        value={`recent::${combo}`}
                        onSelect={() => {
                          onChange(combo);
                          saveToHistory(combo);
                        }}
                      >
                        <History className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate">{combo}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                <CommandGroup heading={recentSets.length > 0 ? "All values" : undefined}>
                  {options.map((opt) => (
                    <CommandItem key={opt} value={opt} onSelect={() => toggleOption(opt)}>
                      <Checkbox
                        checked={selected.includes(opt)}
                        className="mr-2 h-3.5 w-3.5"
                      />
                      {opt}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selected.map((v) => (
              <Badge key={v} variant="secondary" className="text-[10px] gap-0.5 pl-1.5 pr-0.5 py-0">
                {v}
                <button onClick={() => removeOption(v)} className="ml-0.5 rounded hover:bg-muted-foreground/20 p-0.5">
                  <X className="h-2.5 w-2.5" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    );
  }

  // -- Autocomplete --
  if (ph.displayType === "autocomplete") {
    // Merge history + API options; history shown in a separate "Recent" group
    const recentItems = history.filter((h) => !options.includes(h));
    const filteredOptions = options
      .filter((opt) => !value || opt.toLowerCase().includes(value.toLowerCase()))
      .slice(0, 50);
    const filteredRecent = recentItems.filter(
      (h) => !value || h.toLowerCase().includes(value.toLowerCase()),
    );
    const hasAny = filteredRecent.length > 0 || filteredOptions.length > 0;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => setOpen(true)}
              onBlur={() => saveToHistory(value)}
              placeholder={`Enter ${ph.name}…`}
              className="h-8 text-sm"
            />
            {loadingOptions && (
              <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
          </div>
        </PopoverTrigger>
        {hasAny && (
          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command>
              <CommandList>
                {filteredRecent.length > 0 && (
                  <CommandGroup heading="Recent">
                    {filteredRecent.map((opt) => (
                      <CommandItem
                        key={`recent-${opt}`}
                        value={opt}
                        onSelect={() => {
                          onChange(opt);
                          saveToHistory(opt);
                          setOpen(false);
                        }}
                      >
                        <History className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                        {opt}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {filteredOptions.length > 0 && (
                  <CommandGroup heading={filteredRecent.length > 0 ? "Suggestions" : undefined}>
                    {filteredOptions.map((opt) => (
                      <CommandItem
                        key={opt}
                        value={opt}
                        onSelect={() => {
                          onChange(opt);
                          saveToHistory(opt);
                          setOpen(false);
                        }}
                      >
                        {opt}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    );
  }

  // -- Default: text / number / date / datetime-local --
  const inputType =
    ph.displayType === "number" ? "number" :
    ph.displayType === "date" ? "date" :
    ph.displayType === "datetime-local" ? "datetime-local" : "text";

  return (
    <div className="space-y-1">
      <Input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => saveToHistory(value)}
        placeholder={`Enter ${ph.name}…`}
        className="h-8 text-sm"
      />
      {history.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {history.slice(0, 5).map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => {
                onChange(h);
                saveToHistory(h);
              }}
              className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <History className="h-2.5 w-2.5" />
              <span className="truncate max-w-[120px]">{h}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlaceholderInput;
